import { AlertCircleIcon, RefreshCw, SearchIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '..';
import { ContactSupportButton } from './ContactSupportButton';
import { Button } from './ui/button';

interface QuoteErrorDisplayProps {
  error: QuoteError;
  onRetry?: () => void;
}

export function QuoteErrorDisplay({ error, onRetry }: QuoteErrorDisplayProps) {
  switch (error.type) {
    case 'AGE_OUT_OF_RANGE':
      return <AgeOutOfRangeError error={error} />;
    case 'STATE_NOT_SUPPORTED':
      return <StateNotSupportedError error={error} />;
    default:
      return (
        <UnexpectedError
          error={error}
          onRetry={onRetry}
        />
      );
  }
}

interface ErrorStateProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  showErrorIcon?: boolean;
}

function ErrorState({ title, action, children, showErrorIcon }: ErrorStateProps) {
  return (
    <div className="my-auto flex flex-col items-center justify-center gap-4 py-12">
      <div
        className={cn(
          'mb-4 flex h-16 w-16 items-center justify-center rounded-full',
          showErrorIcon ? 'bg-amber-50' : 'bg-primary-50',
        )}
      >
        {showErrorIcon ? (
          <AlertCircleIcon className="h-8 w-8 text-amber-500" />
        ) : (
          <SearchIcon className="text-primary h-8 w-8" />
        )}
      </div>
      <div className="space-y-2 text-center">
        <h3 className="text-primary-dark mb-2 text-lg font-medium">{title}</h3>
        {children}
      </div>
      {action}
    </div>
  );
}

function AgeOutOfRangeError({ error }: { error: QuoteError }) {
  return (
    <ErrorState
      title="We can't show quotes for this age (yet)"
      action={
        <ContactSupportButton
          subject={`Age out of range (${error.clientAge}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="mb-6 max-w-md text-center text-sm text-gray-700">
        Our current quote range is {error.minAge}&ndash;{error.maxAge}. If this client's DOB is
        correct, we can still help.
      </p>
    </ErrorState>
  );
}

function StateNotSupportedError({ error }: { error: QuoteError }) {
  const supportedStatesDisplay = error.supportedStates?.slice(0, 10).join(', ');
  const hasMoreStates = (error.supportedStates?.length || 0) > 10;

  return (
    <ErrorState
      title={`No carrier quotes found for ${error.clientState}`}
      action={
        <ContactSupportButton
          subject={`State not supported (${error.clientState}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="mb-4 max-w-md text-center text-sm text-gray-700">
        Our carrier partners don't currently offer products in{' '}
        <span className="font-medium">{error.clientState}</span>. We can show quotes in:{' '}
        {supportedStatesDisplay}
        {hasMoreStates && ', and more'}.
      </p>

      <p className="mb-6 max-w-md text-center text-xs text-gray-600">
        If you added extra filters, try removing them and re-run quotes. If you already have a
        product quote in hand, we can still help you upload the policy and analyze it against the
        client's LTC plan.
      </p>
    </ErrorState>
  );
}

function UnexpectedError({ error, onRetry }: { error: QuoteError; onRetry?: () => void }) {
  const supportSubject = error.logCode
    ? `Quote loading error (ID: ${error.logCode})`
    : 'Quote loading error - Need assistance';

  return (
    <ErrorState
      showErrorIcon
      title="We're having trouble loading quotes"
      action={
        <div className="flex flex-col gap-3 sm:flex-row">
          {onRetry && (
            <Button
              variant="secondary"
              onClick={onRetry}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          )}
          <ContactSupportButton
            subject={supportSubject}
            variant="outline"
          />
        </div>
      }
    >
      <p className="mb-6 max-w-md text-center text-sm text-gray-700">
        This looks like an issue on our side. Please refresh and try again.
        {error.logCode && (
          <>
            {' '}
            Error ID:{' '}
            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium">
              {error.logCode}
            </code>
          </>
        )}
      </p>
    </ErrorState>
  );
}
